
import { useState, useEffect } from 'react';
import { User, Session } from '@/types/auth';
import { supabase } from '@/integrations/supabase/client';
import { saveAuthData, loadAuthData, clearAuthData, saveSessionData } from '@/utils/authStorage';

/**
 * Hook to manage auth state and session persistence
 */
export function useAuthState() {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state and set up listeners
  useEffect(() => {
    console.log('Setting up auth state listener');
    setLoading(true);

    // Try to load auth data from local storage or cookies first
    const savedAuth = loadAuthData();
    if ((savedAuth.token && savedAuth.user) || savedAuth.session) {
      console.log('Found saved auth data, restoring session');
      // If we have a full session object, use it
      if (savedAuth.session) {
        setSession(savedAuth.session);
        setUser(savedAuth.session.user);
      } else if (savedAuth.user) {
        // Otherwise use individual token and user data
        setSession({
          user: savedAuth.user,
          token: savedAuth.token
        });
        setUser(savedAuth.user);
      }
    }
    
    // Listen for auth events from Supabase
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, currentSession) => {
      console.log('Auth state changed:', event, currentSession?.user?.email);
      
      if (currentSession?.access_token) {
        console.log('Session token obtained, saving session data');
        
        // Map Supabase session to our app session model
        const authUser: User = {
          id: currentSession.user.id,
          email: currentSession.user.email || '',
          username: currentSession.user.user_metadata?.username,
          avatar_url: currentSession.user.user_metadata?.avatar_url
        };
        
        // Create session object
        const sessionObj: Session = {
          user: authUser,
          token: currentSession.access_token
        };
        
        // Store both individual pieces and full session
        saveAuthData(currentSession.access_token, authUser);
        saveSessionData(sessionObj);
        
        // Update state
        setSession(sessionObj);
        setUser(authUser);
        console.log('User authenticated:', authUser);
      } else if (event === 'SIGNED_OUT') {
        console.log('User signed out');
        clearAuthData();
        setSession(null);
        setUser(null);
      }
    });
    
    // Get initial session
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      console.log('Initial session check:', currentSession?.user?.email);
      if (currentSession?.access_token) {
        console.log('Session found, saving session data');
        
        const authUser: User = {
          id: currentSession.user.id,
          email: currentSession.user.email || '',
          username: currentSession.user.user_metadata?.username,
          avatar_url: currentSession.user.user_metadata?.avatar_url
        };
        
        // Create and save full session object
        const sessionObj: Session = {
          user: authUser,
          token: currentSession.access_token
        };
        
        saveAuthData(currentSession.access_token, authUser);
        saveSessionData(sessionObj);
        
        setSession(sessionObj);
        setUser(authUser);
        console.log('Initial user authenticated:', authUser);
      }
      
      setLoading(false);
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return { session, user, loading, setSession, setUser };
}
